import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {Box, Divider, Stack, Tab, Tabs} from "@mui/material";
import Typography from "@mui/material/Typography";
import Logs from "./Logs";
import CasablancaConfigForm from "./CasablancaConfigForm";
import {CasablancaConfig} from "crqlar-react-common/dist/models/integrations/casablanca/casablancaConfig.model";
import {CasablancaService} from "crqlar-react-common/dist/services/integrations/casablanca/casablanca.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";

const CasablancaConfigDetails = () => {

    useTrackScreenVisit('system casablanca config details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'logs'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const casablancaConfigData = useEntity<CasablancaConfig>({
        get: async () => {
            return await CasablancaService.find()
        },
        create: CasablancaService.create,
        update: CasablancaService.update,
        delete: (id) => CasablancaService.delete({id: [id]}),
        isValid: (casablancaConfig) => {
            return !!casablancaConfig.name
        },
        cleanupBeforeSave: (casablancaConfig) => {
            return {
                ...casablancaConfig,
                name: casablancaConfig.name?.trim()
            }
        }
    })

    const casablancaConfig = casablancaConfigData.item

    const handleCreate = () => {
        casablancaConfigData.startCreate({
            enabled: true,
            processingEnabled: true,
            otaMappingRules: {
                autoCreateResourceIfNotFound: true,
                pickAdditionalResourceInfo: true,
                autoCreateServiceForRatePlanIfNotFound: true
            }
        } as CasablancaConfig)
    }

    //endregion State

    const headerSection = (
        <SettingsEntityHeader
            title={casablancaConfig.name}
            data={casablancaConfigData}
            back={() => navigate('/system')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={casablancaConfigData.saving}
                onDelete={() => casablancaConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {casablancaConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {
                    casablancaConfigData.isMissing &&
                    <EmptyState
                        icon={<ElectricalServicesIcon/>}
                        title={t('No integration configured')}
                        description={t('Create the configuration to connect this integration.')}
                    >
                        <Button variant={'contained'} onClick={handleCreate}>
                            {t('Create')}
                        </Button>
                    </EmptyState>
                }

                {
                    !!casablancaConfig.id &&
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        aria-label="tabs"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Tab label={t('Configuration')} value={'config'}/>
                        <Tab label={t('Stats and Logs')} value={'logs'}/>
                    </Tabs>
                }

                {
                    !casablancaConfigData.isMissing && activeTab === 'config' && (
                        <>
                            <CasablancaConfigForm casablancaConfigData={casablancaConfigData}/>

                            {
                                !!casablancaConfig.id &&
                                <>
                                    <Divider sx={{mt: 4}}/>
                                    {dangerZoneSection}
                                </>
                            }
                        </>
                    )
                }

                {
                    activeTab === 'logs' && (
                        <Logs casablancaConfigData={casablancaConfigData}/>
                    )
                }
            </Stack>
        </>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {casablancaConfigData.loading ? <>{headerSection}<DetailsLoading/></> : content}
        </Box>
    );
}

export default CasablancaConfigDetails;
